'use client';
import React, { useMemo } from 'react';
import Icon from '@/components/ui/AppIcon';
import { generateTeeSlots, TeeSlot } from '@/lib/mockData';
import type { BookingState } from './BookingWizard';

interface Props {
  booking: BookingState;
  updateBooking: (u: Partial<BookingState>) => void;
  onNext: () => void;
  onBack: () => void;
}

const slotStatusConfig: Record<TeeSlot['status'], { label: string; cls: string; clickable: boolean }> = {
  available: { label: 'Available', cls: 'tee-slot-available border', clickable: true },
  booked: { label: 'Booked', cls: 'tee-slot-booked border', clickable: false },
  competition: { label: 'Competition', cls: 'tee-slot-competition border', clickable: false },
  maintenance: { label: 'Maintenance', cls: 'bg-red-light text-red-light-foreground border border-red-200 cursor-not-allowed', clickable: false },
  selected: { label: 'Selected', cls: 'tee-slot-selected border', clickable: true },
};

export default function TeeSheetSelector({ booking, updateBooking, onNext, onBack }: Props) {
  const slots = useMemo(() => generateTeeSlots(booking.selectedDate), [booking.selectedDate]);

  const morningSlots = slots.filter(s => parseInt(s.time.split(':')[0]) < 12);
  const afternoonSlots = slots.filter(s => parseInt(s.time.split(':')[0]) >= 12);

  function handleSlotClick(slot: TeeSlot) {
    if (slot.status === 'booked' || slot.status === 'competition' || slot.status === 'maintenance') return;
    updateBooking({ selectedSlot: slot.id, selectedTime: slot.time });
  }

  function renderSlot(slot: TeeSlot) {
    const isSelected = booking.selectedSlot === slot.id;
    const config = slotStatusConfig[isSelected ? 'selected' : slot.status];
    return (
      <button
        key={slot.id}
        onClick={() => handleSlotClick(slot)}
        disabled={!config.clickable && !isSelected}
        className={`rounded-lg p-2 sm:p-2.5 text-left transition-all duration-150 ${config.cls} ${config.clickable || isSelected ? 'hover:scale-[1.02] active:scale-[0.98]' : ''}`}
      >
        <div className="font-semibold text-xs sm:text-sm font-tabular">{slot.time}</div>
        {slot.status === 'available' && (
          <div className="text-xs mt-0.5 opacity-80 hidden sm:block">{slot.availableSpots} spot{slot.availableSpots !== 1 ? 's' : ''}</div>
        )}
        {slot.status === 'booked' && <div className="text-xs mt-0.5 opacity-70 hidden sm:block">Full</div>}
        {slot.status === 'competition' && <div className="text-xs mt-0.5 opacity-70 hidden sm:block">Comp.</div>}
        {slot.status === 'maintenance' && <div className="text-xs mt-0.5 opacity-70 hidden sm:block">Closed</div>}
        {isSelected && <div className="text-xs mt-0.5 font-semibold">✓</div>}
      </button>
    );
  }

  const displayDate = booking.selectedDate
    ? new Date(booking.selectedDate + 'T12:00:00').toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  return (
    <div className="flex flex-col xl:grid xl:grid-cols-3 gap-4 sm:gap-6">
      {/* Main tee sheet grid */}
      <div className="xl:col-span-2 bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-card">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-foreground">Available Tee Times</h2>
            <p className="text-muted-foreground text-xs sm:text-sm mt-0.5">{displayDate}</p>
          </div>
          <button onClick={onBack} className="text-xs sm:text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors shrink-0 ml-2">
            <Icon name="ChevronLeftIcon" size={14} variant="outline" />
            <span className="hidden xs:inline">Change Date</span>
            <span className="xs:hidden">Back</span>
          </button>
        </div>

        {/* Legend — scrollable on mobile */}
        <div className="flex gap-2 sm:gap-3 mb-4 sm:mb-5 pb-4 sm:pb-5 border-b border-border overflow-x-auto scrollbar-thin">
          {[
            { cls: 'tee-slot-available border', label: 'Available' },
            { cls: 'tee-slot-booked border', label: 'Booked' },
            { cls: 'tee-slot-competition border', label: 'Competition' },
            { cls: 'bg-red-light text-red-light-foreground border border-red-200', label: 'Maintenance' },
            { cls: 'tee-slot-selected border', label: 'Selected' },
          ].map((item) => (
            <div key={`tee-legend-${item.label}`} className="flex items-center gap-1 sm:gap-1.5 shrink-0">
              <div className={`w-5 h-3.5 sm:w-6 sm:h-4 rounded text-xs flex items-center justify-center ${item.cls}`} />
              <span className="text-xs text-muted-foreground">{item.label}</span>
            </div>
          ))}
        </div>

        {/* Morning */}
        <div className="mb-5 sm:mb-6">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 sm:mb-3 flex items-center gap-2">
            <Icon name="SunIcon" size={13} variant="outline" />
            Morning (06:00 – 11:50)
          </h3>
          <div className="grid grid-cols-5 xs:grid-cols-6 sm:grid-cols-6 md:grid-cols-8 gap-1.5 sm:gap-2">
            {morningSlots.map(renderSlot)}
          </div>
        </div>

        {/* Afternoon */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 sm:mb-3 flex items-center gap-2">
            <Icon name="CloudIcon" size={13} variant="outline" />
            Afternoon (12:00 – 17:00)
          </h3>
          <div className="grid grid-cols-5 xs:grid-cols-6 sm:grid-cols-6 md:grid-cols-8 gap-1.5 sm:gap-2">
            {afternoonSlots.map(renderSlot)}
          </div>
        </div>
      </div>

      {/* Right Panel — stacked below on mobile */}
      <div className="flex flex-col sm:flex-row xl:flex-col gap-4">
        {/* Summary stats */}
        <div className="bg-card border border-border rounded-2xl p-4 sm:p-5 shadow-card flex-1 xl:flex-none">
          <h3 className="font-semibold text-foreground text-sm mb-3 sm:mb-4">Tee Sheet Summary</h3>
          <div className="grid grid-cols-2 sm:grid-cols-1 gap-2 sm:gap-3">
            {[
              { label: 'Total Slots', value: slots.length.toString() },
              { label: 'Available', value: slots.filter(s => s.status === 'available').length.toString(), color: 'text-green-700' },
              { label: 'Booked', value: slots.filter(s => s.status === 'booked').length.toString(), color: 'text-muted-foreground' },
              { label: 'Competition', value: slots.filter(s => s.status === 'competition').length.toString(), color: 'text-blue-700' },
            ].map((item) => (
              <div key={`tee-summary-${item.label}`} className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">{item.label}</span>
                <span className={`text-sm font-semibold font-tabular ${item.color || 'text-foreground'}`}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Selected time / CTA */}
        {booking.selectedTime ? (
          <div className="bg-card border border-primary/30 rounded-2xl p-4 sm:p-5 shadow-card fade-in flex-1 xl:flex-none">
            <div className="flex items-center gap-2 mb-2 sm:mb-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Icon name="ClockIcon" size={16} variant="outline" className="text-primary" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Selected Tee Time</div>
                <div className="text-xl font-bold text-primary font-tabular">{booking.selectedTime}</div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-3 sm:mb-4">{displayDate}</p>
            <button
              onClick={onNext}
              className="btn-primary w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
            >
              Continue to Details
              <Icon name="ArrowRightIcon" size={16} variant="outline" />
            </button>
          </div>
        ) : (
          <div className="bg-muted/50 border border-border rounded-2xl p-4 sm:p-5 text-center flex-1 xl:flex-none flex flex-col items-center justify-center">
            <Icon name="ClockIcon" size={28} variant="outline" className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Tap a green slot to select your tee time</p>
          </div>
        )}
      </div>
    </div>
  );
}